import type { AddressLike } from '@plato/data/model/address';
import type { GetManyEventsListItem } from '../server/types/infer-trpc-types.ts';
import { getFullAddressString } from './address-helper.ts';

// URLs are limited to 16384 characters for all Google Maps Platform web services
const MAX_URL_LENGTH = 16384;

/**
 * Builds a Google Maps search URL for a single address.
 *
 * URL format: https://www.google.com/maps/search/?api=1&query={encoded address}
 */
export function buildGoogleMapsSearchUrl(address: AddressLike): string | null {
    const query = getFullAddressString(address, undefined, ' ');

    if (!query) {
        return null;
    }

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/**
 * Builds a Google Maps directions URL from a list of events.
 * Events without valid addresses are skipped.
 * Addresses are added until the URL length limit is reached.
 *
 * @param events - List of events to build route from
 * @returns Google Maps URL or null if no valid addresses fit within URL length limit
 */
export function buildGoogleMapsRouteUrl(events: GetManyEventsListItem[]): string | null {
    const baseUrl = 'https://www.google.com/maps/dir//';

    const addresses = events.map((event) => event.address).filter((address) => address && address.length > 0);

    if (addresses.length === 0) {
        return null;
    }

    // Build URL incrementally, stopping when we'd exceed the limit
    const includedAddresses: string[] = [];
    let currentLength = baseUrl.length;

    for (const address of addresses) {
        const encoded = encodeURIComponent(address);
        const additionalLength = encoded.length + (includedAddresses.length > 0 ? 1 : 0); // +1 for separator

        if (currentLength + additionalLength > MAX_URL_LENGTH) {
            break;
        }

        includedAddresses.push(encoded);
        currentLength += additionalLength;
    }

    if (includedAddresses.length === 0) {
        return null;
    }

    return `${baseUrl}${includedAddresses.join('/')}`;
}
